import { Mutex } from 'async-mutex'
import { BaseQueryFn, FetchArgs, fetchBaseQuery, FetchBaseQueryError } from '@reduxjs/toolkit/query'

import appConfig from '~/config/app'
import authService from '~/services/auth.service'
import { updateToken, logoutUser } from '~/auth/slices/auth.slice'
import { cleanNullAndUndefined2, trimObjectProperties } from '~/utils/helpers'
import { refreshToken } from '~/services/axiosInstance.service'

// create a new mutex
const mutex = new Mutex()

/**
 * `prepareHeaders` never receives the request body (RTK 1.9 hands it only
 * `getState | extra | endpoint | type | forced`), so "is this multipart?" cannot be decided inside it.
 * It becomes a constructor argument instead, and `apiPrivate` picks the matching instance per request.
 */
const createBaseQuery = (isMultipart: boolean): ReturnType<typeof fetchBaseQuery> =>
  fetchBaseQuery({
    baseUrl: `${appConfig.homApiUrl}/api`,
    prepareHeaders: (headers) => {
      const accessToken = authService.getAccessToken() // (getState() as RootState).auth.accessToken

      // If we have a token set in state, let's assume that we should be passing it.
      if (accessToken) {
        headers.set('authorization', `Bearer ${accessToken}`)
        // Never on a multipart body: it overwrites the browser's `multipart/form-data; boundary=…`,
        // and without the boundary the server parses no file at all.
        if (!isMultipart) {
          headers.set('content-type', 'application/json')
        }
      }
      return headers
    },
    credentials: 'include',
    mode: 'cors',
  })

const baseQuery = createBaseQuery(false)
const multipartBaseQuery = createBaseQuery(true)

const hasFormDataBody = (args: string | FetchArgs): boolean => typeof args !== 'string' && args.body instanceof FormData

export const apiPrivate: BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> = async (
  rawArgs,
  api,
  extraOptions,
) => {
  // wait until the mutex is available without locking it
  await mutex.waitForUnlock()
  const args = dataTrimmerInterceptor(rawArgs)
  // Resolved once and reused on the retry paths below: replaying a multipart body through the JSON
  // base query would resend it as `application/json`, so an expired token alone would break an upload.
  const query = hasFormDataBody(args) ? multipartBaseQuery : baseQuery

  let result = await query(args, api, extraOptions)
  // console.log('apiPrivate: result: ', result)
  if (
    result.error &&
    result.error.status === 401 &&
    result.error.data &&
    result.error.data.message === 'invalid token'
  ) {
    // checking whether the mutex is locked
    if (!mutex.isLocked()) {
      const release = await mutex.acquire()
      try {
        const refreshResult = await refreshToken()
        if (refreshResult.data) {
          api.dispatch(updateToken(refreshResult.data))
          // retry the initial query
          result = await query(args, api, extraOptions)
        } else {
          api.dispatch(logoutUser())
          // api.dispatch(logoutUser())
        }
      } finally {
        // release must be called once the mutex should be released again.
        release()
      }
    } else {
      // wait until the mutex is available without locking it
      await mutex.waitForUnlock()
      result = await query(args, api, extraOptions)
    }
  }
  return result
}

const dataTrimmerInterceptor = (args: string | FetchArgs): string | FetchArgs => {
  if (typeof args === 'string') {
    return args
  }

  // A FormData has no own enumerable properties, so `trimObjectProperties` would return `{}` and the
  // uploaded file would be dropped here — in the browser, before the request is even built, and with
  // no error to show for it. Multipart bodies pass through untouched.
  if (args.body instanceof FormData) {
    return args
  }

  const argsCopy = { ...args }
  if (argsCopy.body) {
    argsCopy.body = cleanNullAndUndefined2(trimObjectProperties(argsCopy.body, appConfig.skipTrimmingProps))
  }

  return argsCopy
}
